import { useEffect, useMemo, useRef } from 'react';
import { useBannerStore } from '@/stores/bannerStore';
import { useAuthProtection } from '@/features/auth/useAuthProtection';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { getTrialStatus } from '@/helpers/trialStatus';
import { getGracePeriodStatus } from '@/helpers/gracePeriodStatus';
import { MessageRenderContext } from '@/types/message';

const BannerController = () => {
  const { showUpgradeBanner, fetchMessage, message, showTrialBanner, showGracePeriodBanner, isDismissed } =
    useBannerStore();
  const { canCreateNote, isLoading } = useAuthProtection();
  const userData = useAuthStore((state) => state.userData);
  const planFeatures = useCurrentPlanFeatures();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const renderContext = useMemo<MessageRenderContext>(
    () => ({
      planName: planFeatures.planName ?? userData?.subscription?.product_name ?? null,
      aiProcessedCount: planFeatures.aiProcessedCount ?? 0,
      maxAiNotes: planFeatures.maxAiNotes ?? null,
      // Null for everyone who has never launched the desktop app while signed in,
      // which leaves version-gated banners showing rather than hidden.
      desktopVersion: userData?.desktopVersion ?? null,
    }),
    [
      planFeatures.planName,
      planFeatures.aiProcessedCount,
      planFeatures.maxAiNotes,
      userData?.subscription?.product_name,
      userData?.desktopVersion,
    ],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Check grace period first - highest priority
    const gracePeriodStatus = getGracePeriodStatus(userData);

    if (gracePeriodStatus.isInGracePeriod) {
      // Only set the banner if it isn't already showing — calling showGracePeriodBanner
      // updates `message` in the store, which is a dependency of this effect. Calling it
      // unconditionally creates an infinite render loop (NOTEGO-WEB-DN).
      // The setInterval below handles countdown updates.
      if (message?.id !== 'grace-period-banner') {
        showGracePeriodBanner(
          gracePeriodStatus.daysRemaining,
          gracePeriodStatus.hoursRemaining,
          gracePeriodStatus.minutesRemaining,
        );
      }

      // Set up interval to refresh countdown every minute (60000ms)
      // Only if not already set up
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          const updatedStatus = getGracePeriodStatus(userData);
          if (updatedStatus.isInGracePeriod) {
            showGracePeriodBanner(
              updatedStatus.daysRemaining,
              updatedStatus.hoursRemaining,
              updatedStatus.minutesRemaining,
            );
          }
        }, 60000); // Update every minute
      }

      return; // Don't check other conditions if in grace period
    }

    // Clear interval if grace period has ended
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Check trial status - second priority
    const trialStatus = getTrialStatus(userData);

    if (trialStatus.isOnTrial) {
      if (message?.id !== 'trial-banner' && !isDismissed('trial-banner')) {
        showTrialBanner(trialStatus.daysRemaining);
      }
    } else if (!canCreateNote) {
      if (message?.id !== 'upgrade-message' && !isDismissed('upgrade-message')) {
        showUpgradeBanner();
      }
    } else {
      if (
        message?.id === 'upgrade-message' ||
        message?.id === 'trial-banner' ||
        message?.id === 'grace-period-banner'
      ) {
        fetchMessage(renderContext);
      } else if (!message) {
        fetchMessage(renderContext);
      }
    }
  }, [
    canCreateNote,
    isLoading,
    showUpgradeBanner,
    fetchMessage,
    message,
    userData,
    showTrialBanner,
    showGracePeriodBanner,
    isDismissed,
    renderContext,
  ]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return null; // This component renders nothing
};

export default BannerController;
